import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Camera, ChevronDown, Copy, LayoutTemplate, Link2, MoreHorizontal, Play, Redo2, Save, Share2, Undo2, Users } from 'lucide-react'

import ScreenShell from 'components/ScreenShell/ScreenShell'
import { Button } from 'components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu'
import { Input } from 'components/ui/input'
import { Spinner } from 'components/ui/spinner'
import { toast } from 'components/ui/toast-store'
import routes from 'constants/routes'
import { cn } from 'lib/utils'
import { ALL_ELEMENTS, duplicatePresentation, fetchElements, updatePresentation } from 'slices/presentationSlice'
import { isAskMdtAvailable } from 'features/AskMdt/availability'

import { presentationEditPath, presentationPresentPath } from '../featureFlag'
import { useAssetNames } from '../hooks/useAssetNames'
import { useDeckEditor } from '../hooks/useDeckEditor'
import { useResolvedBlocks } from '../hooks/useResolvedBlocks'
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard'
import * as presentationService from '../services/presentationService'
import type { DeckDefinition, ElementDescriptor, EntityRef, Presentation, SlideLayout, Story } from '../types/presentation.types'
import { countSlides, findBlock, findSlide } from '../utils/deckOps'
import { announceTemplateNotices, droppedFromTemplate } from '../utils/templateNotices'
import { AddStoryDialog } from './AddStoryDialog'
import { ElementPicker, type ElementChoice } from './ElementPicker'
import { Inspector } from './Inspector'
import { LayoutPickerDialog } from './LayoutPickerDialog'
import { PortfolioDeckDialog } from './PortfolioDeckDialog'
import { PublishDialog } from './PublishDialog'
import { PartnerSharingDialog } from './PartnerSharingDialog'
import { ShareDialog } from './ShareDialog'
import { SaveDeckTemplateDialog } from './SaveDeckTemplateDialog'
import { SaveTemplateDialog } from './SaveTemplateDialog'
import { SlideCanvas } from './SlideCanvas'
import { SlideSorter } from './SlideSorter'

/**
 * The deck editor (MYD-495): slide sorter | canvas | inspector.
 *
 * State lives in `useDeckEditor` (the deck, undo, dirty). This screen owns
 * only what is on screen: the selection, which dialog is open, and which
 * slides have been looked at (so their thumbnails can show real content).
 *
 * ## Resolving
 *
 * Only the selected slide, its neighbours and slides already visited are
 * resolved. A 100-slide deck would otherwise fire 100 requests on open. The
 * other thumbnails show loading placeholders until visited, and a visited
 * slide stays resolved because the cache is keyed by content.
 */

interface Selection {
	storyId: string | null
	slideId: string | null
	blockId: string | null
}

const NONE: Selection = { storyId: null, slideId: null, blockId: null }

/** The first slide in the deck, or the first story when no story has slides. */
const firstSelection = (deck: DeckDefinition): Selection => {
	for (const story of deck.stories) {
		if (story.slides[0]) return { storyId: story.id, slideId: story.slides[0].id, blockId: null }
	}
	return deck.stories[0] ? { storyId: deck.stories[0].id, slideId: null, blockId: null } : NONE
}

/**
 * `stories[1].slides[3].blocks[0].region: ...` -> the ids at that path, so a
 * save error can take the user straight to what is wrong.
 */
export const selectionFromErrorPath = (message: string, deck: DeckDefinition): Selection | null => {
	const match = message.match(/^stories\[(\d+)\](?:\.slides\[(\d+)\](?:\.blocks\[(\d+)\])?)?/)
	if (!match) return null
	const story = deck.stories[Number(match[1])]
	if (!story) return null
	const slide = match[2] !== undefined ? story.slides[Number(match[2])] : undefined
	const block = slide && match[3] !== undefined ? slide.blocks[Number(match[3])] : undefined
	return { storyId: story.id, slideId: slide?.id ?? null, blockId: block?.id ?? null }
}

const isTyping = (target: EventTarget | null) =>
	target instanceof HTMLElement && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))

export function DeckEditorScreen() {
	const { id = '' } = useParams()
	const navigate = useNavigate()
	const dispatch = useDispatch<any>()
	// "Draft notes" / "Draft text" (MYD-640), where Ask MDT is offered. Only the
	// owner reaches the editor below; a partner gets the read-only view.
	const askAvailable = isAskMdtAvailable(useSelector((state: any) => state.userLogin?.userInfo))
	const editor = useDeckEditor()
	const { deck, dirty, load, markSaved, toDefinition } = editor

	const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading')
	const [presentation, setPresentation] = useState<Presentation | null>(null)
	const [name, setName] = useState('')
	const [selection, setSelection] = useState<Selection>(NONE)
	const [visited, setVisited] = useState<string[]>([])
	const [saving, setSaving] = useState(false)
	const [layoutPicker, setLayoutPicker] = useState<{ mode: 'add'; storyId: string } | { mode: 'change'; slideId: string } | null>(null)
	const [pickerRegion, setPickerRegion] = useState<string | null>(null)
	// Story templates (MYD-505): the Add story dialog, and which story is being saved as one.
	const [addingStory, setAddingStory] = useState(false)
	const [templateStoryId, setTemplateStoryId] = useState<string | null>(null)
	// "Portfolio and its locations" from Add story (MYD-507).
	const [addingPortfolio, setAddingPortfolio] = useState(false)
	// Publish a snapshot (MYD-508).
	const [publishing, setPublishing] = useState(false)
	// Public share links to a published version (MYD-509).
	const [sharing, setSharing] = useState(false)
	// Sharing with chosen partners (MYD-526).
	const [sharingWithPartners, setSharingWithPartners] = useState(false)
	// Save the whole deck as a deck template (MYD-604).
	const [savingAsTemplate, setSavingAsTemplate] = useState(false)

	const catalog: ElementDescriptor[] | undefined = useSelector((state: any) => state.presentations.elementsByScope[ALL_ELEMENTS])
	const elements = useMemo(() => Object.fromEntries((catalog ?? []).map(element => [element.key, element])), [catalog])

	useUnsavedChangesGuard(dirty)

	// --- Load --------------------------------------------------------------
	useEffect(() => {
		let cancelled = false
		setStatus('loading')
		presentationService
			.getPresentation(id)
			.then(loaded => {
				if (cancelled) return
				setPresentation(loaded)
				setName(loaded.name)
				load(loaded)
				setSelection(firstSelection(loaded.definition))
				setStatus('ready')
			})
			.catch(() => !cancelled && setStatus('failed'))
		dispatch(fetchElements(undefined))
		return () => {
			cancelled = true
		}
	}, [id, load, dispatch])

	// --- Keep the selection pointing at something that exists -------------
	useEffect(() => {
		setSelection(current => {
			if (current.slideId && !findSlide(deck, current.slideId)) return firstSelection(deck)
			if (current.storyId && !deck.stories.some(story => story.id === current.storyId)) return firstSelection(deck)
			if (!current.storyId && deck.stories.length) return firstSelection(deck)

			const blockGone = Boolean(current.blockId && !findBlock(deck, current.blockId))
			// A slide dragged into another story takes its story with it.
			const found = current.slideId ? findSlide(deck, current.slideId) : null
			const storyMoved = Boolean(found && found.story.id !== current.storyId)
			if (!blockGone && !storyMoved) return current
			return {
				storyId: storyMoved ? found!.story.id : current.storyId,
				slideId: current.slideId,
				blockId: blockGone ? null : current.blockId,
			}
		})
	}, [deck])

	useEffect(() => {
		if (selection.slideId) setVisited(previous => (previous.includes(selection.slideId!) ? previous : [...previous, selection.slideId!]))
	}, [selection.slideId])

	// --- What to resolve ---------------------------------------------------
	const orderedSlideIds = useMemo(() => deck.stories.flatMap(story => story.slides.map(slide => slide.id)), [deck])
	const requested = useMemo(() => {
		const at = selection.slideId ? orderedSlideIds.indexOf(selection.slideId) : -1
		const neighbours = at >= 0 ? orderedSlideIds.slice(Math.max(0, at - 1), at + 2) : []
		return [...new Set([...visited, ...neighbours])].filter(slideId => orderedSlideIds.includes(slideId))
	}, [visited, selection.slideId, orderedSlideIds])
	const resolved = useResolvedBlocks(deck, requested)

	const refs = useMemo<EntityRef[]>(
		() => deck.stories.flatMap(story => [story.anchor, ...story.slides.flatMap(slide => slide.blocks.map(block => block.source))]).filter(Boolean) as EntityRef[],
		[deck],
	)
	const assetName = useAssetNames(refs)

	const story = deck.stories.find(candidate => candidate.id === selection.storyId) ?? null
	const slide = selection.slideId ? findSlide(deck, selection.slideId)?.slide ?? null : null
	const block = selection.blockId ? findBlock(deck, selection.blockId)?.block ?? null : null

	// --- Save and present --------------------------------------------------
	const save = useCallback(async (): Promise<boolean> => {
		if (saving) return false
		setSaving(true)
		const sent = toDefinition()
		const result = await dispatch(updatePresentation({ id, input: { definition: sent } }))
		setSaving(false)

		if (updatePresentation.rejected.match(result)) {
			const message = String(result.payload ?? 'The presentation could not be saved.')
			toast.error(message, { title: 'Not saved' })
			const at = selectionFromErrorPath(message, sent)
			if (at) setSelection(at)
			return false
		}
		markSaved(result.payload as Presentation, sent)
		setPresentation(result.payload as Presentation)
		return true
	}, [saving, toDefinition, dispatch, id, markSaved])

	const present = async () => {
		if (dirty && !(await save())) return
		navigate(presentationPresentPath(id), { state: { from: presentationEditPath(id) } })
	}

	// A snapshot is of the SAVED deck, so unsaved changes are saved first.
	const openPublish = async () => {
		if (dirty && !(await save())) return
		setPublishing(true)
	}

	// So is a deck template: the server makes it from the saved deck.
	const openSaveAsTemplate = async () => {
		if (dirty && !(await save())) return
		setSavingAsTemplate(true)
	}

	const rename = async () => {
		const trimmed = name.trim()
		if (!presentation || !trimmed || trimmed === presentation.name) return setName(presentation?.name ?? '')
		const result = await dispatch(updatePresentation({ id, input: { name: trimmed } }))
		if (updatePresentation.rejected.match(result)) {
			toast.error(String(result.payload))
			return setName(presentation.name)
		}
		// Only the name: the returned definition is the SAVED one and must not
		// overwrite unsaved edits in the editor.
		setPresentation(previous => (previous ? { ...previous, name: (result.payload as Presentation).name } : previous))
	}

	// --- Keyboard ----------------------------------------------------------
	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			const mod = event.metaKey || event.ctrlKey
			if (!mod) return
			const key = event.key.toLowerCase()
			if (key === 's') {
				event.preventDefault()
				if (dirty) save()
				return
			}
			// In a text field, undo belongs to the field.
			if (isTyping(event.target)) return
			if (key === 'z' && !event.shiftKey) {
				event.preventDefault()
				editor.undo()
			} else if ((key === 'z' && event.shiftKey) || key === 'y') {
				event.preventDefault()
				editor.redo()
			}
		}
		window.addEventListener('keydown', onKeyDown)
		return () => window.removeEventListener('keydown', onKeyDown)
	}, [dirty, save, editor])

	// --- Structure edits ---------------------------------------------------
	const addBlankStory = () => {
		const storyId = editor.addStory({ title: 'New story' })
		setSelection({ storyId, slideId: null, blockId: null })
		setLayoutPicker({ mode: 'add', storyId })
	}
	// "Add story" asks first: blank, or from a template (MYD-505).
	const addStory = () => setAddingStory(true)

	const insertTemplateStories = (stories: Story[], dropped: number) => {
		const made = editor.insertStories(stories)
		if (!made) {
			toast.error('A presentation can hold at most 100 slides.')
			return
		}
		setAddingStory(false)
		setSelection({ storyId: made[0].storyId, slideId: made[0].slideIds[0] ?? null, blockId: null })
		if (dropped > 0) toast.info(`${dropped} ${dropped === 1 ? 'element was' : 'elements were'} left out: they show data you do not have access to.`)
	}

	const pickLayout = (layout: SlideLayout) => {
		if (!layoutPicker) return
		if (layoutPicker.mode === 'change') {
			editor.setLayout(layoutPicker.slideId, layout)
		} else {
			const target = deck.stories.find(candidate => candidate.id === layoutPicker.storyId)
			// Straight after the selected slide when it is in this story, else at the end.
			const at = target && selection.slideId ? target.slides.findIndex(candidate => candidate.id === selection.slideId) : -1
			const slideId = editor.addSlide(layoutPicker.storyId, { layout, index: at >= 0 ? at + 1 : undefined })
			if (slideId) setSelection({ storyId: layoutPicker.storyId, slideId, blockId: null })
			else toast.error('A presentation can hold at most 100 slides.')
		}
		setLayoutPicker(null)
	}

	const deleteStory = async (storyId: string) => {
		const target = deck.stories.find(candidate => candidate.id === storyId)
		if (target && target.slides.length > 0) {
			const ok = await toast.confirm({
				title: 'Delete this story?',
				message: `"${target.title || 'Untitled story'}" and its ${target.slides.length} slide${target.slides.length === 1 ? '' : 's'} will be removed. You can undo this.`,
				confirmLabel: 'Delete',
				variant: 'destructive',
			})
			if (!ok) return
		}
		editor.deleteStory(storyId)
	}

	const placeElement = (choice: ElementChoice) => {
		if (!slide || !pickerRegion) return
		const blockId = editor.addBlock(slide.id, { region: pickerRegion, element: choice.element, config: choice.config, source: choice.source })
		setPickerRegion(null)
		if (blockId) setSelection(current => ({ ...current, blockId }))
		else toast.error('This slide is full. A slide holds at most 6 elements.')
	}

	// --- Render ------------------------------------------------------------
	if (status === 'loading') {
		return (
			<ScreenShell>
				<Spinner type="circular" containerHeight="60vh" />
			</ScreenShell>
		)
	}

	if (status === 'failed' || !presentation) {
		return (
			<ScreenShell>
				<div className="flex flex-col items-center py-24 text-center">
					<h1 className="mb-1 text-lg font-medium text-gray-900">This presentation could not be opened</h1>
					<p className="mb-4 text-sm text-gray-500">It may have been deleted, or it is not shared with you.</p>
					<Link to={routes.PRESENTATIONS} className="text-sm font-medium text-primary hover:underline">
						Back to presentations
					</Link>
				</div>
			</ScreenShell>
		)
	}

	if (!presentation.isOwner) {
		// A partner can present and copy a shared deck, but never edit it.
		const duplicate = async () => {
			const result = await dispatch(duplicatePresentation(id))
			if (duplicatePresentation.rejected.match(result)) return toast.error(String(result.payload))
			navigate(presentationEditPath((result.payload as Presentation).id))
		}
		return (
			<ScreenShell>
				<div className="flex flex-col items-center py-24 text-center">
					<h1 className="mb-1 text-lg font-medium text-gray-900">{presentation.name}</h1>
					<p className="mb-5 max-w-sm text-sm text-gray-500">This presentation was shared with you. You can present it, or make your own copy to edit.</p>
					<div className="flex gap-2">
						<Button onClick={() => navigate(presentationPresentPath(id), { state: { from: routes.PRESENTATIONS } })}>
							<Play className="mr-1 h-3.5 w-3.5" /> Present
						</Button>
						<Button variant="ghost" isOutline onClick={duplicate}>
							<Copy className="mr-1 h-3.5 w-3.5" /> Make a copy
						</Button>
					</div>
				</div>
			</ScreenShell>
		)
	}

	return (
		<ScreenShell>
			<div className="flex h-full flex-col">
				<div className="flex h-14 shrink-0 items-center gap-3 border-b border-gray-200 bg-white px-4">
					<Link to={routes.PRESENTATIONS} className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900" aria-label="Back to presentations">
						<ArrowLeft className="h-4 w-4" />
					</Link>
					<Input
						value={name}
						maxLength={140}
						aria-label="Presentation name"
						onChange={event => setName(event.target.value)}
						onBlur={rename}
						onKeyDown={event => {
							if (event.key === 'Enter') (event.target as HTMLInputElement).blur()
							if (event.key === 'Escape') setName(presentation.name)
						}}
						className="h-8 max-w-sm border-transparent text-base font-semibold hover:border-input focus:border-input"
					/>
					<span className={cn('text-xs', dirty ? 'text-warning' : 'text-muted-foreground')} aria-live="polite">
						{saving ? 'Saving...' : dirty ? 'Unsaved changes' : 'All changes saved'}
					</span>

					<div className="ml-auto flex items-center gap-2">
						<button type="button" onClick={editor.undo} disabled={!editor.canUndo} className="rounded p-1.5 text-gray-600 hover:bg-gray-100 disabled:opacity-30" aria-label="Undo" title="Undo (Ctrl+Z)">
							<Undo2 className="h-4 w-4" />
						</button>
						<button type="button" onClick={editor.redo} disabled={!editor.canRedo} className="rounded p-1.5 text-gray-600 hover:bg-gray-100 disabled:opacity-30" aria-label="Redo" title="Redo (Ctrl+Shift+Z)">
							<Redo2 className="h-4 w-4" />
						</button>
						<Button variant="ghost" isOutline onClick={save} disabled={!dirty || saving} title="Save (Ctrl+S)">
							<Save className="mr-1 h-3.5 w-3.5" /> Save
						</Button>
						<Button variant="ghost" isOutline onClick={openPublish} disabled={saving || orderedSlideIds.length === 0} title="Publish a snapshot to share">
							<Camera className="mr-1 h-3.5 w-3.5" /> Publish
						</Button>
						{/* Two ways to share (MYD-526): with partners in the app, or a
						    public link to a published version (MYD-509). */}
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="ghost" isOutline title="Share this deck">
									<Share2 className="mr-1 h-3.5 w-3.5" /> Share
									<ChevronDown className="ml-1 h-3.5 w-3.5" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuItem onClick={() => setSharingWithPartners(true)}>
									<Users className="mr-2 h-4 w-4" />
									With partners...
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => setSharing(true)} disabled={orderedSlideIds.length === 0}>
									<Link2 className="mr-2 h-4 w-4" />
									Public link...
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="ghost" isOutline aria-label="More actions" title="More">
									<MoreHorizontal className="h-4 w-4" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuItem onClick={openSaveAsTemplate} disabled={saving || deck.stories.length === 0}>
									<LayoutTemplate className="mr-2 h-4 w-4" />
									Save deck as template...
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
						<Button onClick={present} disabled={saving || orderedSlideIds.length === 0}>
							<Play className="mr-1 h-3.5 w-3.5" /> Present
						</Button>
					</div>
				</div>

				<div className="flex min-h-0 flex-1">
					<aside className="w-60 shrink-0 overflow-y-auto border-r border-gray-200 bg-gray-50" aria-label="Slides">
						<SlideSorter
							deck={deck}
							blocks={resolved.blocks}
							selectedStoryId={selection.storyId}
							selectedSlideId={selection.slideId}
							assetName={assetName}
							onSelectSlide={slideId => setSelection({ storyId: findSlide(deck, slideId)?.story.id ?? null, slideId, blockId: null })}
							onSelectStory={storyId => setSelection({ storyId, slideId: null, blockId: null })}
							onAddSlide={storyId => setLayoutPicker({ mode: 'add', storyId })}
							onAddStory={addStory}
							onMoveSlide={editor.moveSlide}
							onMoveStory={editor.moveStory}
							onDuplicateSlide={slideId => {
								const copy = editor.duplicateSlide(slideId)
								if (copy) setSelection(current => ({ ...current, slideId: copy, blockId: null }))
							}}
							onDeleteSlide={editor.deleteSlide}
							onDeleteStory={deleteStory}
							onSaveStoryAsTemplate={setTemplateStoryId}
						/>
					</aside>

					{/* The canvas has its own keyboard; Ask MDT's Cmd/Ctrl+K stays out of it (MYD-621). */}
					<main className="min-w-0 flex-1 bg-gray-100 p-6" data-ask-mdt-hotkey="off">
						{slide ? (
							<SlideCanvas
								slide={slide}
								blocks={resolved.blocks}
								theme={deck.theme}
								selectedBlockId={selection.blockId}
								onSelectBlock={blockId => setSelection(current => ({ ...current, blockId }))}
								onAddElement={region => setPickerRegion(region)}
							/>
						) : (
							<div className="flex h-full flex-col items-center justify-center text-center">
								<p className="mb-3 text-sm text-gray-500">{story ? 'This story has no slides yet.' : 'Add a story to start building.'}</p>
								<Button onClick={() => (story ? setLayoutPicker({ mode: 'add', storyId: story.id }) : addStory())}>
									{story ? 'Add a slide' : 'Add a story'}
								</Button>
							</div>
						)}
					</main>

					<aside className="w-80 shrink-0 overflow-y-auto border-l border-gray-200 bg-white" aria-label="Inspector">
						<Inspector
							editor={editor}
							deck={deck}
							story={story}
							slide={slide}
							block={block}
							resolvedBlock={block ? resolved.blocks[block.id] : undefined}
							sharedDeck={presentation.visibility === 'shared'}
							draftDeckId={askAvailable && presentation.isOwner ? id : null}
							elements={elements}
							assetName={assetName}
							onDeselectBlock={() => setSelection(current => ({ ...current, blockId: null }))}
							onChangeLayout={slideId => setLayoutPicker({ mode: 'change', slideId })}
						/>
					</aside>
				</div>
			</div>

			<AddStoryDialog
				open={addingStory}
				onClose={() => setAddingStory(false)}
				onBlank={() => {
					setAddingStory(false)
					addBlankStory()
				}}
				onInsert={insertTemplateStories}
				onPortfolio={() => {
					setAddingStory(false)
					setAddingPortfolio(true)
				}}
			/>

			<PortfolioDeckDialog
				open={addingPortfolio}
				mode={{ kind: 'insert', existingSlides: countSlides(deck) }}
				onClose={() => setAddingPortfolio(false)}
				onInsert={(stories, dropped) => {
					insertTemplateStories(stories, dropped)
					setAddingPortfolio(false)
				}}
			/>

			<PublishDialog open={publishing} presentationId={id} deck={deck} elements={elements} onClose={() => setPublishing(false)} />
			<PartnerSharingDialog
				open={sharingWithPartners}
				presentationId={id}
				presentationName={presentation.name}
				onClose={() => setSharingWithPartners(false)}
				onSaved={saved => setPresentation(previous => (previous ? { ...previous, visibility: saved.visibility } : previous))}
			/>
			<ShareDialog
				open={sharing}
				presentationId={id}
				onClose={() => setSharing(false)}
				onPublish={() => {
					setSharing(false)
					openPublish()
				}}
			/>

			<SaveTemplateDialog
				open={templateStoryId !== null}
				story={deck.stories.find(candidate => candidate.id === templateStoryId) ?? null}
				onClose={() => setTemplateStoryId(null)}
				onSaved={templateName => {
					setTemplateStoryId(null)
					toast.success(`Saved "${templateName}". It is in Add story, under templates.`)
				}}
			/>

			<SaveDeckTemplateDialog
				open={savingAsTemplate}
				presentationId={id}
				deckName={presentation.name}
				deck={deck}
				onClose={() => setSavingAsTemplate(false)}
				onSaved={result => {
					setSavingAsTemplate(false)
					toast.success(`Saved "${result.template.name}". Start a new deck from it under New presentation.`)
					announceTemplateNotices(result, droppedFromTemplate)
				}}
			/>

			<LayoutPickerDialog
				open={layoutPicker !== null}
				onClose={() => setLayoutPicker(null)}
				onPick={pickLayout}
				title={layoutPicker?.mode === 'change' ? 'Change layout' : 'Add a slide'}
				current={layoutPicker?.mode === 'change' ? slide?.layout : undefined}
			/>

			<ElementPicker
				open={pickerRegion !== null}
				onClose={() => setPickerRegion(null)}
				region={pickerRegion}
				layout={slide?.layout ?? null}
				storyAnchor={story?.anchor ?? null}
				anchorName={assetName(story?.anchor ?? null)}
				onPick={placeElement}
			/>
		</ScreenShell>
	)
}

export default DeckEditorScreen
